import {
  ArtifactWithParameters,
  CreateArtifact,
  UpdateArtifact,
  ArtifactItem,
  ArtifactItemState,
  ArtifactItemTransition,
  ArtifactItemProcess,
  Artifact,
} from '../types/artifacts';
import { CreateArtifactItemState, CreateArtifactItemStateFromPrime } from '../types/artifactItems';
import { api } from './api';
import { API_ENDPOINTS } from './api/config';
import {
  ProcessArtifactRecommendation,
  ProcessRecommendation,
  PeerArtifactRecommendation,
  StateRecommendation,
  TransitionRecommendation,
} from '../types/recommendation';

export interface CreateArtifactModelData {
  artifact: CreateArtifact;
  items: ArtifactItem[];
  states: ArtifactItemState[];
  transitions: ArtifactItemTransition[];
  processes: ArtifactItemProcess[];
}

class ArtifactService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = '/artifacts';
  }

  async getArtifacts(): Promise<ArtifactWithParameters[]> {
    const response = await fetch(`${this.baseUrl}`);
    if (!response.ok) {
      throw new Error('Failed to fetch artifacts');
    }
    return response.json();
  }

  async getArtifact(id: string): Promise<ArtifactWithParameters> {
    const response = await fetch(`${this.baseUrl}/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch artifact');
    }
    return response.json();
  }

  async createArtifact(artifact: CreateArtifact): Promise<ArtifactWithParameters> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(artifact),
    });
    if (!response.ok) {
      throw new Error('Failed to create artifact');
    }
    return response.json();
  }

  async updateArtifact(id: string, artifact: UpdateArtifact): Promise<ArtifactWithParameters> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(artifact),
    });
    if (!response.ok) {
      throw new Error('Failed to update artifact');
    }
    return response.json();
  }

  async deleteArtifact(id: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete artifact');
    }
  }

  async getArtifactParameters(id: string): Promise<ArtifactWithParameters> {
    const response = await fetch(`${this.baseUrl}/${id}/parameters`);
    if (!response.ok) {
      throw new Error('Failed to fetch artifact parameters');
    }
    return response.json();
  }

  async getRecommendedParameters(artifactId: string): Promise<ArtifactWithParameters> {
    const response = await fetch(`${this.baseUrl}/${artifactId}/recommended-parameters`);
    if (!response.ok) {
      throw new Error('Failed to fetch recommended parameters');
    }
    return response.json();
  }

  async createArtifactModel(data: CreateArtifactModelData): Promise<ArtifactWithParameters> {
    const response = await fetch(`${this.baseUrl}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data.artifact),
    });
    if (!response.ok) {
      throw new Error('Failed to create artifact model');
    }
    return response.json();
  }

  async getRecommendedChildArtifacts(artifactId: string): Promise<
    Array<{
      id: string;
      name: string;
      description: string;
      artifact_type_id: string;
      reason: string;
      confidence_score: number;
      source_artifact_id: string;
    }>
  > {
    const response = await api.get(API_ENDPOINTS.ARTIFACTS.RECOMMENDED_CHILD_ARTIFACTS(artifactId));
    return response.data.map((rec: Record<string, unknown>) => ({
      id: `${rec.artifact_type_id}-${(rec.name as string).replace(/\s+/g, '-').toLowerCase()}`,
      name: rec.name,
      description: rec.description,
      artifact_type_id: rec.artifact_type_id,
      reason: rec.reason,
      confidence_score: rec.confidence_score,
      source_artifact_id: rec.source_artifact_id,
    }));
  }

  async getRecommendedProcessArtifacts(
    artifactId: string
  ): Promise<ProcessArtifactRecommendation[]> {
    const response = await api.get(
      API_ENDPOINTS.ARTIFACTS.RECOMMENDED_PROCESS_ARTIFACTS(artifactId)
    );
    return response.data;
  }

  async getRecommendedStates(artifactId: string): Promise<StateRecommendation[]> {
    const response = await api.get(API_ENDPOINTS.ARTIFACTS.RECOMMENDED_STATES(artifactId));
    return response.data;
  }

  async getRecommendedTransitions(artifactId: string): Promise<TransitionRecommendation[]> {
    const response = await api.get(API_ENDPOINTS.ARTIFACTS.RECOMMENDED_TRANSITIONS(artifactId));
    return response.data;
  }

  async getRecommendedProcesses(artifactId: string): Promise<ProcessRecommendation[]> {
    const response = await api.get(API_ENDPOINTS.ARTIFACTS.RECOMMENDED_PROCESSES(artifactId));
    return response.data;
  }

  async getRecommendedPeerArtifacts(artifactId: string): Promise<PeerArtifactRecommendation[]> {
    const response = await api.get(API_ENDPOINTS.ARTIFACTS.RECOMMENDED_PEER_ARTIFACTS(artifactId));
    return response.data;
  }

  async create(artifact: Partial<Artifact>): Promise<Artifact> {
    const response = await api.post('/artifacts', {
      ...artifact,
      artifact_tree_id: artifact.artifact_tree_ref,
      artifact_tree_ref: undefined,
    });
    return response.data;
  }

  async createState(
    artifactId: string,
    stateData: CreateArtifactItemState | CreateArtifactItemStateFromPrime
  ) {
    const response = await api.post(`/artifacts/${artifactId}/states`, stateData);
    return response.data;
  }

  async createStateFromPrime(artifactId: string, stateData: CreateArtifactItemStateFromPrime) {
    const response = await api.post(
      `/artifacts/recommendations/artifacts/${artifactId}/states/from-prime`,
      stateData
    );
    return response.data;
  }

  async createTransition(
    artifactId: string,
    transition: Partial<ArtifactItemTransition>
  ): Promise<ArtifactItemTransition> {
    const response = await api.post(`/artifacts/${artifactId}/transitions`, transition);
    return response.data;
  }

  // Individual recommendation creation methods
  async createStateFromRecommendation(
    artifactId: string,
    state: CreateArtifactItemState
  ): Promise<ArtifactItemState> {
    const response = await api.post(
      `/artifacts/${artifactId}/recommendations/states`,
      state
    );
    return response.data;
  }

  async createTransitionFromRecommendation(
    artifactId: string,
    transition: Partial<ArtifactItemTransition>
  ): Promise<ArtifactItemTransition> {
    const response = await api.post(
      `/artifacts/${artifactId}/recommendations/transitions`,
      transition
    );
    return response.data;
  }

  async createProcessFromRecommendation(
    artifactId: string,
    process: Partial<ArtifactItemProcess>
  ): Promise<ArtifactItemProcess> {
    const response = await api.post(`/artifacts/recommendations/processes/bulk`, {
      processes: [
        {
          artifact_id: artifactId,
          prime_process_id: process.metadata?.prime_process_id,
          name: process.name,
          description: process.description,
          process_type: process.process_type,
          duration: process.duration,
          duration_unit: process.duration_unit,
        },
      ],
    });
    return response.data[0];
  }

  async createArtifactFromRecommendation(
    artifactId: string,
    artifact: Partial<Artifact>
  ): Promise<Artifact> {
    const response = await api.post(
      `/artifacts/${artifactId}/recommendations/artifacts`,
      artifact
    );
    return response.data;
  }

  // Bulk recommendation creation methods
  async createStatesFromRecommendations(
    states: Array<{ artifact_ref: string; prime_state_ref: string }>
  ): Promise<ArtifactItemState[]> {
    try {
      const response = await api.post<ArtifactItemState[]>(
        '/artifacts/recommendations/states/bulk',
        { states }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating states from recommendations:', error);
      throw error;
    }
  }

  async createTransitionsFromRecommendations(
    artifactId: string,
    transitions: Partial<ArtifactItemTransition>[]
  ): Promise<ArtifactItemTransition[]> {
    const response = await api.post(`/artifacts/recommendations/transitions/bulk`, {
      transitions,
    });
    return response.data;
  }

  async createProcessesFromRecommendations(
    artifactId: string,
    processes: Partial<ArtifactItemProcess>[]
  ): Promise<ArtifactItemProcess[]> {
    const response = await api.post(`/artifacts/recommendations/processes/bulk`, {
      processes,
    });
    return response.data;
  }

  async createArtifactsFromRecommendations(
    artifactId: string,
    artifacts: Partial<Artifact>[]
  ): Promise<Artifact[]> {
    const response = await api.post(`/artifacts/recommendations/artifacts/bulk`, {
      artifacts,
    });
    return response.data;
  }

  // Complete set creation
  async createCompleteSetFromRecommendations(
    artifactId: string,
    data: {
      states?: CreateArtifactItemState[];
      transitions?: Partial<ArtifactItemTransition>[];
      processes?: Partial<ArtifactItemProcess>[];
      artifacts?: Partial<Artifact>[];
    }
  ): Promise<{
    states: ArtifactItemState[];
    transitions: ArtifactItemTransition[];
    processes: ArtifactItemProcess[];
    artifacts: Artifact[];
  }> {
    const response = await api.post(
      `/artifacts/${artifactId}/recommendations/complete-set`,
      data
    );
    return response.data;
  }
}

export const artifactService = new ArtifactService();
